import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  serverTimestamp,
  updateDoc,
  type DocumentData,
  type Firestore,
  type QuerySnapshot,
} from 'firebase/firestore'
import {
  deleteObject,
  getDownloadURL,
  ref as storageRef,
  uploadBytes,
  type FirebaseStorage,
} from 'firebase/storage'
import { editadoDe } from '@domain/receipt'
import type { MatchStatus, Receipt, ReceiptItem, ReceiptStatus } from '@domain/receipt'
import type { ReceiptRepository } from '@domain/ports/receipt-repository'
import {
  booleanOr,
  optionalCents,
  optionalText,
  requireCents,
  requireInteger,
  requireOneOf,
  requireText,
  subDocument,
  textOr,
  toIso,
} from './converters'
import { HOUSEHOLD_ID } from './household-repository'

/**
 * As notas fiscais: a imagem no Storage e o documento no Firestore.
 *
 * ## A imagem sobe ANTES do documento, e a ordem importa
 *
 * O documento aponta para o caminho da imagem, e a Function o usa para baixar.
 * Criar o documento primeiro daria uma nota que a Function tentaria processar
 * antes de o arquivo existir — e ela falharia com "sem imagem" numa nota que
 * está subindo. Na ordem inversa, o pior caso é uma imagem órfã no Storage se o
 * documento falhar: custa bytes, e não uma nota quebrada.
 *
 * ## O caminho inclui um id aleatório, e não o nome do arquivo
 *
 * Duas fotos chamadas `IMG_0001.jpg` são o caso comum de quem fotografa cupom, e
 * o nome de arquivo do celular não é único nem entre duas pessoas da mesma casa.
 * Reaproveitar o caminho sobrescreveria a nota anterior — e `Receipt.md` diz que
 * *"uma imagem de nota nunca é apagada depois de `confirmed`"*.
 */

const RECEIPT_STATUSES = ['uploaded', 'processing', 'extracted', 'confirmed', 'failed'] as const
const MATCH_STATUSES = ['pending', 'confirmed', 'rejected', 'skipped'] as const

/** O truque de `comment-repository.ts`: o array de runtime cobre a união. */
type Equals<A, B> =
  (<T>() => T extends A ? 1 : 2) extends <T>() => T extends B ? 1 : 2 ? true : false
const _statusCobremAUniao: Equals<ReceiptStatus, (typeof RECEIPT_STATUSES)[number]> = true
const _matchesCobremAUniao: Equals<MatchStatus, (typeof MATCH_STATUSES)[number]> = true
void _statusCobremAUniao
void _matchesCobremAUniao

function receiptsPath(): string {
  return `households/${HOUSEHOLD_ID}/receipts`
}

function itemsPath(receiptId: string): string {
  return `${receiptsPath()}/${receiptId}/items`
}

function notaDe(id: string, data: DocumentData): Receipt {
  const onde = `receipts/${id}`
  const extraido = data['extracted']
  return {
    id,
    householdId: textOr(data['householdId'], HOUSEHOLD_ID),
    transactionId: optionalText(data['transactionId']),
    // Ausente é `null`: o campo é novo e toda nota gravada antes dele não o tem.
    categoryId: optionalText(data['categoryId']),
    imageStoragePath: textOr(data['imageStoragePath'], ''),
    thumbnailUrl: optionalText(data['thumbnailUrl']),
    status: requireOneOf(data['status'], RECEIPT_STATUSES, `${onde}.status`),
    extracted:
      extraido === null || extraido === undefined
        ? null
        : (() => {
            const campos = subDocument(extraido)
            return {
              merchantName: optionalText(campos['merchantName']),
              merchantCnpj: optionalText(campos['merchantCnpj']),
              issuedAt: optionalText(campos['issuedAt']),
              totalCents: optionalCents(campos['totalCents'], `${onde}.extracted.totalCents`),
              itemCount: typeof campos['itemCount'] === 'number' ? campos['itemCount'] : 0,
              accessKey: optionalText(campos['accessKey']),
            }
          })(),
    provider: optionalText(data['provider']),
    processedAt: toIso(data['processedAt']),
    error: optionalText(data['error']),
    // `serverTimestamp()` chega `null` no snapshot local, antes de o servidor
    // responder — e a tela abre no instante do upload. Mesma razão de
    // `list-repository.ts`.
    createdAt: toIso(data['createdAt']) ?? '',
    updatedAt: toIso(data['updatedAt']) ?? '',
  }
}

function itemDe(receiptId: string, id: string, data: DocumentData): ReceiptItem {
  const onde = `receipts/${receiptId}/items/${id}`
  return {
    id,
    householdId: textOr(data['householdId'], HOUSEHOLD_ID),
    receiptId,
    lineNumber: requireInteger(data['lineNumber'], `${onde}.lineNumber`),
    // Ausente é `false`: a linha lida é a esmagadora maioria, e toda linha
    // gravada antes deste campo veio de leitura. Ver `ReceiptItem.manual`.
    manual: booleanOr(data['manual'], false),
    rawText: requireText(data['rawText'], `${onde}.rawText`),
    rawCode: optionalText(data['rawCode']),
    quantity: typeof data['quantity'] === 'number' ? data['quantity'] : 1,
    unit: optionalText(data['unit']),
    unitPriceCents: requireCents(data['unitPriceCents'], `${onde}.unitPriceCents`),
    totalCents: requireCents(data['totalCents'], `${onde}.totalCents`),
    // Ausente é ZERO, e não erro: o campo é novo e toda linha gravada antes
    // dele existe sem ele. Exigi-lo faria a revisão de qualquer nota antiga
    // morrer no conversor.
    discountCents: typeof data['discountCents'] === 'number' ? data['discountCents'] : 0,
    barcode: optionalText(data['barcode']),
    productId: optionalText(data['productId']),
    matchId: optionalText(data['matchId']),
    matchStatus: requireOneOf(data['matchStatus'], MATCH_STATUSES, `${onde}.matchStatus`),
    categoryId: optionalText(data['categoryId']),
    corrected: booleanOr(data['corrected'], false),
    // Ausente é "sem correção", pelo mesmo motivo do desconto; e a correção
    // quebrada também — a linha volta ao lido em vez de a revisão morrer.
    edited: editadoDe(data['edited']),
    createdAt: toIso(data['createdAt']) ?? '',
    updatedAt: toIso(data['updatedAt']) ?? '',
  }
}

export function createFirebaseReceiptRepository(
  db: Firestore,
  storage: FirebaseStorage,
  /** Só para o caminho da imagem ser único. Ver o cabeçalho. */
  novoId: () => string,
): ReceiptRepository {
  return {
    create: async (file, input) => {
      // A imagem primeiro. Ver o cabeçalho.
      const caminho = `households/${HOUSEHOLD_ID}/receipts/${novoId()}`
      const destino = storageRef(storage, caminho)
      // **`|| 'image/jpeg'`, e não `file.type` seco.** `storage.rules` exige
      // `contentType.matches('image/.*')`, e alguns seletores de arquivo do
      // Android entregam tipo vazio — o que daria um 403 idêntico ao de quem
      // não é membro, que é o erro mais caro de diagnosticar que este projeto
      // já teve (decisão 177). A foto vem de um `accept="image/*"` e, quando o
      // preparo funcionou, já é JPEG de verdade.
      await uploadBytes(destino, file, { contentType: file.type || 'image/jpeg' })

      const criada = await addDoc(collection(db, receiptsPath()), {
        householdId: HOUSEHOLD_ID,
        transactionId: input.transactionId,
        imageStoragePath: caminho,
        thumbnailUrl: await getDownloadURL(destino),
        // O único status que a regra aceita do cliente.
        status: 'uploaded',
        extracted: input.extracted,
        provider: null,
        processedAt: null,
        error: null,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      })
      return criada.id
    },

    list: async () => {
      // Sem `orderBy`: a coleção é pequena (uma nota por compra de mercado) e
      // ordenar em memória evita um índice para uma tela que já lê tudo.
      const snap = await getDocs(collection(db, receiptsPath()))
      return snap.docs
        .map((d) => notaDe(d.id, d.data()))
        .sort((a, b) => b.createdAt.localeCompare(a.createdAt))
    },

    observe: (id, onChange, onError) =>
      onSnapshot(
        doc(db, receiptsPath(), id),
        (snap) => {
          onChange(snap.exists() ? notaDe(snap.id, snap.data()) : null)
        },
        onError,
      ),

    observeItems: (receiptId, onChange, onError) =>
      onSnapshot(
        collection(db, itemsPath(receiptId)),
        (snap: QuerySnapshot) => {
          onChange(
            snap.docs
              .map((d) => itemDe(receiptId, d.id, d.data()))
              .sort((a, b) => a.lineNumber - b.lineNumber),
          )
        },
        onError,
      ),

    /**
     * A linha acrescentada à mão.
     *
     * **`lineNumber` sai do maior que existe**, e não de um contador nosso: a
     * lista é ordenada por ele, e uma linha manual tem de cair no fim, depois
     * do que a nota imprimiu. Duas adições no mesmo instante podem empatar, e
     * empate aqui é ordem de exibição — o `sort` do JavaScript é estável, então
     * elas ficam na ordem em que chegaram.
     *
     * **`matchStatus: 'skipped'`** porque é o que o domínio já define como "sai
     * do catálogo mas continua contando no total da nota". Sem produto, ela não
     * gera ponto de preço nem aprendizado, e é isso que se quer: frete não é
     * mercadoria.
     */
    addItem: async (receiptId, input) => {
      const existentes = await getDocs(collection(db, itemsPath(receiptId)))
      const ultimo = existentes.docs.reduce((maior, d) => {
        const n = d.get('lineNumber') as unknown
        return typeof n === 'number' && n > maior ? n : maior
      }, 0)

      const criado = await addDoc(collection(db, itemsPath(receiptId)), {
        householdId: HOUSEHOLD_ID,
        receiptId,
        lineNumber: ultimo + 1,
        manual: true,
        rawText: input.name,
        rawCode: null,
        quantity: 1,
        unit: null,
        // Unitário igual ao total porque a quantidade é 1 — assim a linha
        // "fecha" na conferência de quantidade × unitário como qualquer outra.
        unitPriceCents: input.totalCents,
        totalCents: input.totalCents,
        // O desconto de linha é do cupom, e este cupom não tem esta linha.
        discountCents: 0,
        barcode: null,
        productId: null,
        matchId: null,
        matchStatus: 'skipped',
        categoryId: input.categoryId,
        corrected: false,
        edited: null,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      })
      return criado.id
    },

    removeItem: async (receiptId, itemId) => {
      await deleteDoc(doc(db, itemsPath(receiptId), itemId))
    },

    updateItem: async (receiptId, itemId, patch) => {
      const campos: DocumentData = { updatedAt: serverTimestamp() }
      if (patch.productId !== undefined) campos['productId'] = patch.productId
      if (patch.matchStatus !== undefined) campos['matchStatus'] = patch.matchStatus
      if (patch.categoryId !== undefined) campos['categoryId'] = patch.categoryId
      if (patch.corrected !== undefined) campos['corrected'] = patch.corrected
      if (patch.discountCents !== undefined) campos['discountCents'] = patch.discountCents
      if (patch.edited !== undefined) campos['edited'] = patch.edited
      if (patch.manualLine !== undefined) {
        campos['rawText'] = patch.manualLine.name
        campos['totalCents'] = patch.manualLine.totalCents
        // A quantidade de uma linha manual é 1, então o unitário acompanha o
        // total — senão ela passaria a "não fechar" por causa da edição.
        campos['unitPriceCents'] = patch.manualLine.totalCents
      }
      await updateDoc(doc(db, itemsPath(receiptId), itemId), campos)
    },

    linkTransaction: async (receiptId, transactionId) => {
      await updateDoc(doc(db, receiptsPath(), receiptId), {
        transactionId,
        updatedAt: serverTimestamp(),
      })
    },

    setCategory: async (receiptId, categoryId) => {
      await updateDoc(doc(db, receiptsPath(), receiptId), {
        categoryId,
        updatedAt: serverTimestamp(),
      })
    },

    remove: async (receiptId) => {
      // A ordem importa. O documento por ÚLTIMO: ele é quem guarda o caminho da
      // imagem, e apagá-lo primeiro deixaria o arquivo no Storage sem ninguém
      // que soubesse onde ele está. É a mesma ordem de `clearImage`, pelo mesmo
      // motivo — e o inverso deixa lixo que nada alcança.
      const ref = doc(db, receiptsPath(), receiptId)
      const nota = await getDoc(ref)
      const caminho = nota.get('imageStoragePath') as string | undefined

      // Subcoleção não some com o documento pai: no Firestore o filho continua
      // existindo, invisível, e voltaria a aparecer se alguém recriasse a nota
      // com o mesmo id. São ~20 itens por nota; um `deleteDoc` cada resolve.
      const itens = await getDocs(collection(db, itemsPath(receiptId)))
      await Promise.all(itens.docs.map((d) => deleteDoc(d.ref)))

      if (caminho !== undefined && caminho !== '') {
        await deleteObject(storageRef(storage, caminho)).catch((erro: unknown) => {
          // Arquivo já ausente não é falha: o que importa é ele não sobrar. E
          // parar aqui deixaria a nota viva com os itens já apagados, que é
          // pior do que uma imagem órfã.
          console.error('remove: a imagem não foi apagada do Storage', erro)
        })
      }

      await deleteDoc(ref)
    },

    confirm: async (receiptId) => {
      // Só o status. O breakdown, os pontos de preço e o aprendizado são de
      // `onReceiptConfirmed` — ver a porta.
      await updateDoc(doc(db, receiptsPath(), receiptId), {
        status: 'confirmed',
        updatedAt: serverTimestamp(),
      })
    },
  }
}
